/**
 * Nav — `Oprappo Design System.dc.html § 03 কাঠামো · নেভিগেশন`. Two pieces.
 *
 * AppBar: `background:#0E3A45; padding:13px 15px; gap:11px`, back chevron 18px in #fff
 * (accessible name `পিছনে যান`, from § 01), title `600 17px/1.75` in #fff.
 *
 * TabBar: `background:#fff; border-top:1px solid #E2DED5;
 * box-shadow:0 -2px 14px rgba(14,58,69,.14); grid-template-columns:repeat(5,1fr)`.
 * Each cell `padding:9px 2px 10px`, centred, 22px icon, label at `margin-top:2px`.
 *   active   `600 13px/1.75` #0E3A45, icon stroked #0E3A45
 *   inactive `500 13px/1.75` #5B6B69, icon stroked #8B9795
 *   ai       `500 13px/1.75` #8A5A0C, icon stroked #8A5A0C — "The AI tab is the only
 *            amber item in the bar, and the only amber item on most screens."
 *
 * The shadow on this bar is drawn `.14`, which docs/02 names `shadow-sheet`, while
 * docs/02's `shadow-bottombar` is `.10`. Transcribed as drawn. See README § deviations.
 */
import type { ReactNode } from 'react';
import { Icon } from './Icon.js';
import type { IconName } from './Icon.js';
import { IconButton } from './IconButton.js';
import { cx } from './internal/cx.js';

export interface AppBarProps {
  /** Bangla screen title, e.g. `রিচার্জ`. */
  titleBn: string;
  onBack?: (() => void) | undefined;
  /** Trailing slot — bell, avatar, three-dots. Use `IconButton` so the name is fixed. */
  trailing?: ReactNode | undefined;
  className?: string | undefined;
}

export function AppBar({ titleBn, onBack, trailing, className }: AppBarProps): JSX.Element {
  return (
    <div className={cx('bg-teal px-[15px] py-[13px] flex items-center gap-[11px]', className)}>
      {onBack !== undefined ? <IconButton control="back" size={18} stroke="#ffffff" onClick={onBack} /> : null}
      <div className="font-anek font-semibold text-[17px] leading-[1.75] text-white flex-1 min-w-0">
        {titleBn}
      </div>
      {trailing}
    </div>
  );
}

/** The five tabs drawn in § 03, in order. */
export type TabBarKey = 'home' | 'explore' | 'ai' | 'activity' | 'account';

interface TabDef {
  key: TabBarKey;
  icon: IconName;
  labelBn: string;
}

/**
 * Labels exactly as drawn in § 03. `অ্যাক্টিভিটি` is the measured worst case behind the
 * 13px floor: 57.8px in a 72px cell at 360px width (docs/02 § type).
 */
export const TAB_BAR_ITEMS: readonly TabDef[] = [
  { key: 'home', icon: 'home', labelBn: 'হোম' },
  { key: 'explore', icon: 'explore', labelBn: 'এক্সপ্লোর' },
  { key: 'ai', icon: 'ai', labelBn: 'এআই' },
  { key: 'activity', icon: 'activity', labelBn: 'অ্যাক্টিভিটি' },
  { key: 'account', icon: 'account', labelBn: 'অ্যাকাউন্ট' },
];

export interface TabBarProps {
  activeKey: TabBarKey;
  onChange?: ((key: TabBarKey) => void) | undefined;
  className?: string | undefined;
}

/**
 * ── TRANSCRIBED FROM THE PRODUCT FRAMES, NOT FROM THE DESIGN SYSTEM PAGE ────────
 *
 * This component used to follow `Oprappo Design System.dc.html § 03`. Every screen that
 * actually draws the bar disagrees with that page, and they all agree with each other:
 * B1 @376, B5 @597, E1 @1469 and F1 @1723 in `Oprappo P0 Screens.dc.html` are
 * byte-identical, and they differ from § 03 in five ways.
 *
 *   shadow      `rgba(14,58,69,.1)`  — the `bottombar` token, not `sheet` (.14)
 *   padding     `8px 0 9px` on the GRID, not `9px 2px 10px` per cell
 *   icon        21px, not 22px
 *   inactive    `#5B6B69`, not `#8B9795`
 *   cell        `text-align:center` with the svg INLINE, not a flex box
 *
 * The last one is the one that moves pixels, and it is not a style preference. An inline
 * replaced element sits on the text baseline, so the drawn icon's line box is 31px where
 * a flex box gives 22px — nine pixels, handed to the scroll region above, which pulls
 * extra rows of every list into view. Measured on /explore: design nav top 604.95 height
 * 73.75, built nav top 612.25 height 66.75.
 *
 * The colour change is the one worth pausing on. `#8B9795` is `greyBorder`, which
 * packages/tokens marks BORDERS AND SVG STROKES ONLY because it fails 4.5:1 as text — and
 * § 03 had it stroking an icon directly above a label. `#5B6B69` is `muted`, which the
 * design uses for both the icon and its label, and which clears the floor at 4.65:1 on
 * sand-deep. So the frames are not merely normative here, they are also right.
 *
 * A note for whoever reads this next: the discrepancy was reported once during the build,
 * checked with a grep for `.10`, found absent, and dismissed. The file writes `.1`. The
 * report was correct and the check was wrong, and a correct component was nearly
 * "corrected" on the strength of it.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function TabBar({ activeKey, onChange, className }: TabBarProps): JSX.Element {
  return (
    <nav
      className={cx(
        'bg-white border-t border-border shadow-bottombar grid grid-cols-5 pt-[8px] pb-[9px]',
        className,
      )}
    >
      {TAB_BAR_ITEMS.map((t) => {
        const active = t.key === activeKey;
        const amber = t.key === 'ai';
        // The design never draws the AI tab in its active state — in every frame across
        // every file it is amber and 500. Amber is kept in both states so the frame note
        // ("the only amber item in the bar") stays true; only the weight changes.
        // See README § deviations.
        const stroke = amber ? '#8A5A0C' : active ? '#0E3A45' : '#5B6B69';
        return (
          <button
            key={t.key}
            type="button"
            aria-current={active ? 'page' : undefined}
            onClick={() => onChange?.(t.key)}
            className="p-0 text-center bg-transparent border-0 rounded-none"
          >
            {/* `inline`, deliberately — see the note above on the baseline. */}
            <div className="text-center">
              <Icon name={t.icon} size={21} stroke={stroke} className="inline" />
            </div>
            <div
              className={cx(
                'font-anek text-[13px] leading-[1.75] mt-[2px]',
                active ? 'font-semibold' : 'font-medium',
                amber ? 'text-amber-text' : active ? 'text-teal' : 'text-muted',
              )}
            >
              {t.labelBn}
            </div>
          </button>
        );
      })}
    </nav>
  );
}

export const Nav = { AppBar, TabBar } as const;
